import { useFormContext } from 'react-hook-form';

import { PlusIcon } from 'lucide-react';
import { z } from 'zod';

import { DatePicker } from '@/components/ui/DatePicker';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';

export type SelectOption = {
    value: string;
    label: string;
};

export const createTaskFormSchema = z
    .object({
        name: z.string().min(1, 'Task name is required'),
        party: z
            .string()
            .min(1, 'Party is required')
            .refine((val) => ['lessor', 'camo', 'mro'].includes(val), {
                message: 'Party is required',
            }),
        assigneeId: z.string().min(1, 'Assignee is required'),
        priority: z
            .string()
            .min(1, 'Priority is required')
            .refine((val) => ['high', 'medium', 'low'].includes(val), {
                message: 'Priority is required',
            }),
        startDate: z.date({
            error: 'Start date is required',
        }),
        endDate: z.date({
            error: 'End date is required',
        }),
    })
    .refine((data) => data.startDate <= data.endDate, {
        message: 'Start date must be on or before end date',
        path: ['startDate'],
    })
    .refine((data) => data.endDate >= data.startDate, {
        message: 'End date must be on or after start date',
        path: ['endDate'],
    });

export type CreateTaskFormValues = z.infer<typeof createTaskFormSchema>;

interface Props {
    parties: SelectOption[];
    persons: SelectOption[];
    priorities: SelectOption[];
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (data: CreateTaskFormValues) => void;
}

export function CreateTaskDialog({
    parties,
    persons,
    priorities,
    isOpen,
    onOpenChange,
    onSubmit,
}: Props) {
    const form = useFormContext<CreateTaskFormValues>();
    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                <Button className="ml-auto" testId="AddTaskButton">
                    <PlusIcon />
                    <span>Add task</span>
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="CreateTaskDialog">
                <DialogHeader>
                    <DialogTitle data-testid="dialog-title">
                        Add New Task
                    </DialogTitle>
                    <DialogDescription>
                        Create a new task for the project timeline.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className="space-y-4 py-4">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Task Name</FormLabel>
                                    <FormControl>
                                        <Input
                                            placeholder="e.g., Document Review"
                                            data-testid="CreateTaskDialog__nameInput"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex gap-4">
                            <FormField
                                control={form.control}
                                name="party"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Responsible Party</FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger data-testid="CreateTaskDialog__partySelect">
                                                    <SelectValue placeholder="Select party" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {parties.map((party) => (
                                                    <SelectItem
                                                        key={party.value}
                                                        value={party.value}
                                                    >
                                                        {party.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="assigneeId"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>
                                            Responsible Person
                                        </FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            value={field.value}
                                        >
                                            <FormControl>
                                                <SelectTrigger data-testid="CreateTaskDialog__assigneeSelect">
                                                    <SelectValue placeholder="Select person" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                {persons.map((person) => (
                                                    <SelectItem
                                                        key={person.value}
                                                        value={person.value}
                                                    >
                                                        {person.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <FormField
                            control={form.control}
                            name="priority"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Priority</FormLabel>
                                    <Select
                                        onValueChange={field.onChange}
                                        value={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger data-testid="CreateTaskDialog__prioritySelect">
                                                <SelectValue placeholder="Select priority" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {priorities.map((priority) => (
                                                <SelectItem
                                                    key={priority.value}
                                                    value={priority.value}
                                                >
                                                    {priority.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className="flex gap-4">
                            <FormField
                                control={form.control}
                                name="startDate"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>Start Date</FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                className="w-full"
                                                date={field.value}
                                                onDateChange={field.onChange}
                                                testId="CreateTaskDialog__startDateInput"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name="endDate"
                                render={({ field }) => (
                                    <FormItem className="flex-1">
                                        <FormLabel>End Date</FormLabel>
                                        <FormControl>
                                            <DatePicker
                                                className="w-full"
                                                date={field.value}
                                                onDateChange={field.onChange}
                                                testId="CreateTaskDialog__endDateInput"
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="CreateTaskDialog__cancelButton"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            data-testid="CreateTaskDialog__submitButton"
                        >
                            Add Task
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
